/**
 * Las variables de la plantilla, debajo de ella en «Qué les decís»: qué dato va en cada hueco y, al lado, a quiénes les
 * llega y cómo les queda (la previa real con tres contactos de la audiencia, que arma el backend). Con contactos sin el
 * dato y sin decisión, se dice a cuántos y qué elegir. Lo que se guarda y se calcula vive en `useVariablesCampana`. El
 * estado de la plantilla en Meta va una sola vez, arriba, junto a su previa (`PasoPlantilla`).
 */
import { Loader2 } from "lucide-react";
import type { CampoDisponible, Chequeo, MapeoVariables, PlantillaDetalle, Previa, VariablePlantilla } from "../../types";
import type { sinDecision } from "../../logic/mapeo";
import { VALOR_VACIO } from "../../logic/mapeo";
import { mediaConVariables } from "../../logic/plantilla";
import { imagenPorCampana } from "../../logic/imagenes";
import { destinoParaMostrar } from "../../logic/formato";
import { MapeoVariablesForm } from "../MapeoVariablesForm";
import { QuienesQuedanAfuera } from "../ResumenChequeo";
import { PreviaWhatsApp } from "../PreviaWhatsApp";
import { AvisoRojo } from "./AvisoRojo";
import { num, plural } from "../../../campana-nueva/utils/format";

export function PasoVariables({
  detalle, errorDetalle, variables, campos, mapeo, onMapeo, errores, sinResolver,
  calculando, error, onReintentar, chequeo, fatigaDias, fatigaMaximo, previa,
}: {
  /** la plantilla elegida (undefined mientras llega) */
  detalle?: PlantillaDetalle; errorDetalle: string;
  /** las que se mapean: sin las de los botones que cuentan clics */
  variables: VariablePlantilla[]; campos: CampoDisponible[];
  mapeo: MapeoVariables; onMapeo: (m: MapeoVariables) => void; errores: Record<string, string>;
  /** las variables con contactos sin el dato y sin decisión (`sinDecision`) */
  sinResolver: ReturnType<typeof sinDecision>;
  calculando: boolean; error: string;
  /** «Reintentar» el cálculo: sólo con el mapeo completo (undefined, sin el botón) */
  onReintentar?: () => void;
  chequeo: Chequeo | null; fatigaDias: number; fatigaMaximo: number; previa: Previa | null;
}) {
  const etiquetaCampo = (c: string) => campos.find((x) => x.campo === c)?.etiqueta || c;
  return (
    <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_minmax(0,340px)]">
      <div className="space-y-4">
        {!detalle ? (
          errorDetalle ? <AvisoRojo aviso={{ texto: errorDetalle }} /> : <div className="h-40 rounded-xl bg-slate-100 animate-pulse" />
        ) : (
          <MapeoVariablesForm
            variables={variables}
            campos={campos}
            mapeo={mapeo}
            sinResolver={sinResolver}
            onChange={onMapeo}
            errores={errores}
            imagen={imagenPorCampana(detalle.mediaUrl)}
          />
        )}
        {sinResolver.map((v) => (
          <p key={v.variable} className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800" role="alert">
            A <strong>{num(v.contactos)}</strong> {plural(v.contactos, "contacto le falta", "contactos les falta")} «{etiquetaCampo(v.campo)}». Elegí: usar un texto en su lugar, o no mandarles.
          </p>
        ))}
        {error && (
          <div className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 flex items-center justify-between gap-3" role="alert">
            <span>{error}</span>
            {onReintentar && (
              <button type="button" onClick={onReintentar} className="font-semibold underline whitespace-nowrap">Reintentar</button>
            )}
          </div>
        )}
      </div>
      <div className="space-y-3" aria-live="polite">
        <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500 flex items-center gap-2">
          Así les llega {calculando && <span className="inline-flex items-center gap-1 normal-case font-medium text-slate-400"><Loader2 size={11} className="animate-spin" /> Calculando…</span>}
        </p>
        {chequeo && <QuienesQuedanAfuera chequeo={chequeo} fatigaDias={fatigaDias} fatigaMaximo={fatigaMaximo} />}
        {/* la clave lleva la posición: dos ids de cliente cortos («cli1», «cli2», del sí de la base de ventas) se
            enmascaran igual («c***») */}
        {previa?.ejemplos?.length ? previa.ejemplos.map((e, i) => (
          <div key={`${i}-${e.personaIdEnmascarado}`} className="space-y-1">
            <p className="text-xs text-slate-500">
              {e.nombre || "Sin nombre"} · <span className="font-mono">{destinoParaMostrar(e.destino, false)}</span>
              {e.usoDefault?.length > 0 && <span className="ml-2 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-amber-50 text-amber-700">usó el texto por defecto</span>}
              {Object.values(e.variables || {}).includes(VALOR_VACIO) && <span className="ml-2 text-[10px] font-bold uppercase px-1.5 py-0.5 rounded bg-amber-50 text-amber-700">con una variable vacía</span>}
            </p>
            <PreviaWhatsApp compacto texto={e.texto} mediaUrl={mediaConVariables(previa.plantilla.mediaUrl, e.variables)} botones={previa.plantilla.botones || []}
              pie={previa.plantilla.pie} encabezadoTexto={previa.plantilla.encabezadoTexto} />
          </div>
        )) : !calculando && (
          <p className="text-sm text-slate-400">{chequeo ? "No hay contactos para mostrar de ejemplo." : "Completá las variables para ver cómo queda."}</p>
        )}
      </div>
    </div>
  );
}
